import { useRef, useEffect, useCallback, useState } from 'react';

const useDragScroll = ({ horizontalFactor = 2, dragThreshold = 10 } = {}) => {
  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const scrollLeft = useRef(0);
  const animationFrame = useRef(null);
  const [didDrag, setDidDrag] = useState(false);

  // Eventi per mouse
  const onMouseDown = useCallback((e) => {
    if (!scrollRef.current) return;
    isDragging.current = true;
    setDidDrag(false);
    scrollRef.current.classList.add('active', 'dragging');
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
    document.body.style.userSelect = 'none';
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;

    if (Math.abs(x - startX.current) > dragThreshold) {
      setDidDrag(true);
    }

    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }

    animationFrame.current = requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = scrollLeft.current - walk;
      }
    });
  }, [dragThreshold]);

  const onMouseUpOrLeave = useCallback(() => {
    if (!scrollRef.current) return;
    isDragging.current = false;
    scrollRef.current.classList.remove('active', 'dragging');
    document.body.style.userSelect = 'auto';
  }, []);

  // Eventi per touch (li lasciamo definiti, ma li utilizzerai solo se volessi attivare il drag anche su mobile)
  const onTouchStart = useCallback((e) => {
    if (!scrollRef.current) return;
    isDragging.current = true;
    setDidDrag(false);
    scrollRef.current.classList.add('active', 'dragging');
    startX.current = e.touches[0].pageX - scrollRef.current.offsetLeft;
    startY.current = e.touches[0].pageY - scrollRef.current.offsetTop;
    scrollLeft.current = scrollRef.current.scrollLeft;
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!isDragging.current || !scrollRef.current) return;

    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const y = e.touches[0].pageY - scrollRef.current.offsetTop;

    const deltaX = Math.abs(x - startX.current);
    const deltaY = Math.abs(y - startY.current);

    // Se il movimento orizzontale è significativamente maggiore di quello verticale
    if (deltaX > deltaY * horizontalFactor) {
      e.preventDefault();
    } else {
      return;
    }

    if (deltaX > dragThreshold) {
      setDidDrag(true);
    }

    const walk = (x - startX.current) * 1.5;

    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }

    animationFrame.current = requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = scrollLeft.current - walk;
      }
    });
  }, [horizontalFactor, dragThreshold]);

  const onTouchEndOrCancel = useCallback(() => {
    if (!scrollRef.current) return;
    isDragging.current = false;
    scrollRef.current.classList.remove('active', 'dragging');
  }, []);

  // Prevenzione del click in caso di drag
  const onClick = useCallback((e) => {
    if (didDrag) {
      e.preventDefault();
      e.stopPropagation();
    }
    setDidDrag(false);
  }, [didDrag]);

  // BIND/UNBIND EVENTS solo se siamo su desktop
  useEffect(() => {
    // Verifica se il dispositivo ha un puntatore "fine"
    if (typeof window !== 'undefined' && !window.matchMedia('(pointer: fine)').matches) {
      // Se il dispositivo non supporta un puntatore preciso (es. smartphone/tablet), non attivo il drag
      return;
    }

    if (!scrollRef.current) return;

    const currentRef = scrollRef.current;

    // Aggiungo gli eventi per il mouse
    currentRef.addEventListener('mousedown', onMouseDown);
    currentRef.addEventListener('mouseleave', onMouseUpOrLeave);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUpOrLeave);

    // Se invece volessi attivare anche il touch su desktop (ad esempio per dispositivi 2-in-1), potresti aggiungere anche questi:
    // currentRef.addEventListener('touchstart', onTouchStart, { passive: false });
    // currentRef.addEventListener('touchmove', onTouchMove, { passive: false });
    // currentRef.addEventListener('touchend', onTouchEndOrCancel);
    // currentRef.addEventListener('touchcancel', onTouchEndOrCancel);

    return () => {
      currentRef.removeEventListener('mousedown', onMouseDown);
      currentRef.removeEventListener('mouseleave', onMouseUpOrLeave);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUpOrLeave);

      // Rimuovi anche gli event listener per il touch se li hai aggiunti
      // currentRef.removeEventListener('touchstart', onTouchStart);
      // currentRef.removeEventListener('touchmove', onTouchMove);
      // currentRef.removeEventListener('touchend', onTouchEndOrCancel);
      // currentRef.removeEventListener('touchcancel', onTouchEndOrCancel);
    };
  }, [onMouseDown, onMouseMove, onMouseUpOrLeave]);

  return {
    scrollRef,
    didDrag,
  };
};

export default useDragScroll;
